import React, { useContext, useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { Context as AuthContext } from '../../contexts/AuthContext'
import { useIsDesktop } from '../../hooks'
import { imageUrls } from '../../constants'
import { PageHeader, FullPageLoader, Svg } from '../../components'
import { deletedBookMetaText, retentionText } from '../../utilities/deletedBookViewHelpers'
import styles from './DeletedAccountingBooksPage.module.scss'

const RETENTION_DAYS = 365

const DeletedAccountingBooksPage = () => {
  const { state: authState } = useContext(AuthContext)
  const { group_id } = useParams()
  const isDesktop = useIsDesktop()
  const [books, setBooks] = useState([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)

  const getBooks = useCallback(() => {
    if (!authState || !authState.api) { return Promise.resolve() }

    return authState.api.getDeletedAccountingBooks(group_id)
      .then((res) => setBooks(res.data.accounting_books))
      .catch((err) => console.log(err))
      .finally(() => setLoading(false))
  }, [authState, group_id])

  useEffect(() => { getBooks() }, [getBooks])

  const restore = (book) => {
    if (busyId) { return }
    setBusyId(book.id)
    setErrorMessage(null)

    authState.api.restoreAccountingBook(group_id, book.id)
      .then(() => getBooks())
      .catch((err) => {
        // 期限已過或帳本已達上限，後端會回傳原因
        const message = err.response && err.response.data && err.response.data.error_message
        setErrorMessage(message || '還原失敗，請稍後再試')
        getBooks()
      })
      .finally(() => setBusyId(null))
  }

  const renderBook = (book) => {
    const isBusy = busyId === book.id
    const expired = (book.days_left || 0) === 0

    return (
      <div className={styles.book} key={book.id}>
        <div className={styles.row}>
          <img className={styles.icon} src={imageUrls[book.image_id || 0]} alt="" />
          <div className={styles.info}>
            <div className={styles.name}>{book.name}</div>
            <div className={styles.meta}>{deletedBookMetaText(book)}</div>
          </div>
          <span className={[styles.retention, expired ? styles.expired : ''].join(' ')}>
            {retentionText(book)}
          </span>
        </div>
        <button
          className={styles.restoreBtn}
          disabled={expired || isBusy}
          onClick={() => restore(book)}>
          還原帳本
        </button>
      </div>
    )
  }

  return (
    <div className={styles.bg}>
      { !isDesktop &&
        <PageHeader
          faicon="faChevronLeft"
          link={`/liff_entry/groups/${group_id}/accounting_books`}>
          刪除的帳本
        </PageHeader>
      }

      {
        loading
          ? <FullPageLoader />
          : (
            <div className={styles.body}>
              <p className={styles.pageSub}>
                刪除的帳本會保留 <b>{RETENTION_DAYS}</b> 天，期限內還原後帳款紀錄都會回來。
              </p>

              { errorMessage && <div className={styles.error}>{errorMessage}</div> }

              {
                books.length === 0
                  ? (
                    <div className={styles.empty}>
                      <span className={styles.emptyIcon}>
                        <Svg icon='delete' size='24' className='gray900'/>
                      </span>
                      <p>這個群組還沒有刪除任何帳本。</p>
                    </div>
                  )
                  : <div className={styles.list}>{ books.map(renderBook) }</div>
              }
            </div>
          )
      }
    </div>
  )
}

export default DeletedAccountingBooksPage
